import * as React from 'react'
import { UserContext } from '../../Context/User'
import { StoresContext } from '../../Context/Stores'
import { API_URL, fetchCfg } from '../../../config'
import SVG from '../../SVG'
import Error from '../../Error'
import Checkbox from '../../Checkbox'
import InfoNote from '../../InfoNote'
import StorefrontSelector from '../../ChannelSelector'
import Loading from '../../Loading'
import { createFileName, downloadBlob, fromLocalStorage } from '../../../common'
import Plausible from '../../../Plausible-dummy'

const { trackEvent } = Plausible()

let savedChannels = fromLocalStorage('selectedChannel') || {}

const CategoriesDownload = () => {
  const user = React.useContext(UserContext)
  const storesCtx = React.useContext(StoresContext)
  const { store, channel } = storesCtx

  const [channelId, setChannelId] = React.useState(
    savedChannels[store.bc_hash] || 1
  )

  const storefronts = store.channels.filter((c) => c.type === 'storefront')

  // If user selects a different store, find the last selected channel.
  React.useEffect(() => {
    setChannelId(savedChannels[store.bc_hash] || 1)
  }, [store.bc_hash])

  /* "Remember" the user's column selections */
  const getColumnDefaultsFromLocalStorage = () => {
    const savedColumns = fromLocalStorage('categoryColumns') || {}
    delete savedColumns.template
    delete savedColumns.parent_id
    delete savedColumns.custom_url
    return {
      name: true,
      description: false,
      views: true,
      sort_order: true,
      page_title: true,
      meta_keywords: true,
      meta_description: true,
      image_url: true,
      is_visible: true,
      search_keywords: true,
      default_product_sort: true,
      url: true,
      PRODUCT_COUNT: false,
      ...savedColumns,
    }
  }

  const [formData, setFormData] = React.useState(
    getColumnDefaultsFromLocalStorage()
  )
  const [waiting, setWaiting] = React.useState(false)
  const [error, setError] = React.useState(null)

  const handleCheck = (event) => {
    const { id, checked } = event?.target
    setFormData((formData) => ({ ...formData, [id]: checked }))
    setError(null)
  }

  const onChannelSelect = (id) => {
    // Remember the selected channel for this store.
    localStorage.setItem(
      'selectedChannel',
      JSON.stringify({
        ...savedChannels,
        [store.bc_hash]: id,
      })
    )
    // Reload the saved selected channels
    savedChannels = fromLocalStorage('selectedChannel') || {}
    setChannelId(id)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
  }

  const runTool = async () => {
    setWaiting(true)
    trackEvent('Categories download')

    localStorage.setItem('categoryColumns', JSON.stringify(formData))
    await downloadFile()
    setWaiting(false)
  }

  const downloadFile = async () => {
    setError(null)
    let res
    try {
      res = await fetch(
        `${API_URL}/${store.bc_hash}/${channelId}/categories/export`,
        {
          ...fetchCfg(user.csrfToken),
          method: 'post',
          body: JSON.stringify(formData),
        }
      )
      if (res.status === 200) {
        const fileBlob = await res.blob()
        downloadBlob(
          fileBlob,
          createFileName('categories', store.bc_hash, channel.name, 'csv')
        )
        return
      }
      const data = await res.json()
      setError(data.error)
    } catch (err) {
      console.error(res)
      setError('Something went wrong')
    }
  }

  const disabled = !user.authenticated || !channel?.id

  // Wait until store context loads and we've found the last selected channel for it.
  if (!store?.channels?.map((c) => c.id).includes(channelId)) {
    return <Loading />
  }

  return (
    <div className="download">
      <div className="flex-1 border-b border-gray-200 mb-4">
        <h5 className="text-3xl leading-6 font-semibold text-gray-900 flex items-center mt-0">
          <span className="text-indigo-600">
            <SVG name="categories" size={54} lineWidth={1.5} />
          </span>
          <div className="pl-2 flex items-end">
            Categories
            {/*
            <div className="bg-white p-1 rounded-full">
              <img
                src={`https://s2.googleusercontent.com/s2/favicons?domain_url=${channel?.site?.url}`}
                alt={channel?.name}
                className="w-5 h-5"
              />
            </div>
            <span className="text-base">{channel?.name}</span>
            */}
          </div>
        </h5>
      </div>

      <form method="post" action="#" onSubmit={handleSubmit}>
        <InfoNote title="MSF aware">
          This tool is Multi-Storefront aware. It will only download categories
          that have been assigned to the storefront you select below.
        </InfoNote>
        <InfoNote title="Read-only columns">
          The all uppercase columns will contain read-only values. You can't
          change these values upon import with this tool.
        </InfoNote>
        <div className="pt-4">
          <div className="font-bold text-lg">Download categories</div>
        </div>

        {storefronts.length > 1 && (
          <>
            <div className="pt-4 pb-2 text-sm text-gray-500">
              Storefront
            </div>
            <StorefrontSelector
              store={store}
              channelId={channelId}
              onChannelSelect={onChannelSelect}
            />
          </>
        )}

        <div className="pt-4 text-sm text-gray-500">
          Select the columns to include in your CSV file download.
        </div>

        <div className="mt-6 grid grid-cols-2">
          <Checkbox
            key="CATEGORY_ID"
            id="CATEGORY_ID"
            value={true}
            onChange={handleCheck}
            disabled={true}
          />
          <Checkbox
            key="CATEGORY_UUID"
            id="CATEGORY_UUID"
            value={true}
            onChange={handleCheck}
            disabled={true}
          />
          <Checkbox
            key="parent_id"
            id="parent_id"
            value={true}
            onChange={handleCheck}
            disabled={true}
          />
          <Checkbox
            key="tree_id"
            id="tree_id"
            value={true}
            onChange={handleCheck}
            disabled={true}
          />
          {Object.keys(formData).map((id) => (
            <Checkbox
              key={id}
              id={id}
              label={
                id === 'PRODUCT_COUNT' ? 'PRODUCT_COUNT (will take longer)' : id
              }
              value={formData[id]}
              onChange={handleCheck}
            />
          ))}
        </div>

        {/* <div className="flex-1 p-4 md:ml-20">
          <p className="text-sm leading-6 text-gray-700">
            Learn about each of the fields and their restrictions by expanding
            "Schema" in the "Request Body" section of the{' '}
            <a
              className="link"
              target="_blank"
              rel="noopener noreferrer"
              href="https://developer.bigcommerce.com/api-reference/catalog/catalog-api/category/updatecategory"
            >
              BigCommerce Update Category documentation
            </a>
            .
          </p>
        </div> */}

        {error && <Error msg={error} />}

        <div className="mt-2">
          <div className="w-full py-4">
            <button
              onClick={runTool}
              disabled={disabled}
              type="button"
              className={`flex items-center justify-center py-2 px-4 border border-transparent text-lg font-medium rounded-md text-white ${
                disabled ? 'bg-gray-500' : 'bg-blue-600'
              } ${
                !disabled && 'hover:bg-blue-500'
              } focus:outline-none focus:border-blue-700 focus:shadow-outline-blue active:bg-blue-700 transition duration-150 ease-in-out`}
            >
              {waiting ? (
                <SVG name="spinner" size={32} />
              ) : (
                <SVG name="download" size={32} lineWidth={1} />
              )}
              <div className="ml-2">Download</div>
            </button>
          </div>
        </div>

        <div className="px-1 py-2 rounded-md opacity-70">
          <div className="flex items-center">
            <span className="text-gray-500">
              <SVG name="info" size={16} />
            </span>
            <div className="ml-3">
              <p className="text-xs text-gray-500">
                It may take several seconds for your CSV file to be generated
                and start downloading.
              </p>
            </div>
          </div>
        </div>
      </form>
    </div>
  )
}

export default CategoriesDownload

/* Complicated streamsaver solution that fails in Firefox 
    const downloadFile = () => {
      const fileStream = streamSaver.createWriteStream(createFileName('categories', storesCtx.store.bc_hash, 'csv'))
  
      fetch(`${API_URL}/${storesCtx.store.bc_hash}/categories/export`, {
        ...fetchCfg(user.csrfToken),
        method: 'post',
        body: JSON.stringify(formData)
      }).then(res => {
        const readableStream = res.body
  
        // more optimized
        if (window.WritableStream && readableStream.pipeTo) {
          return readableStream.pipeTo(fileStream)
            .then(() => console.log('done writing'))
        }
  
        window.writer = fileStream.getWriter()
  
        const reader = res.body.getReader()
        const pump = () => reader.read()
          .then(res => res.done
            ? window.writer.close()
            : window.writer.write(res.value).then(pump))
  
        pump()
      })
    } */

/* Using streamSaver instead
  const XdownloadFile = () => {
    let url = `${API_URL}/categories/${storesCtx.store.bc_hash}`
    let element = document.createElement('a')
    element.setAttribute('href', url)
    element.setAttribute('download', `categories.csv`)
    element.style.display = 'none'
    document.body.appendChild(element)
    element.click()
    document.body.removeChild(element)
  }
  */
